import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useExamLock } from '@/hooks/useExamLock';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { TestEngine } from '@/components/test/TestEngine';
import { nom35EarlyExitCheck } from '@/lib/nom35';
import {
  NOM35_G1_ITEMS_FOR_ENGINE,
  NOM35_G2_ITEMS_FOR_ENGINE,
  NOM35_G3_ITEMS_FOR_ENGINE,
} from '@/config/nom35Items';
import { scoreNom35G1, scoreNom35G2, scoreNom35G3 } from '@/lib/nom35';
import { getTestDisplayName } from '@/config/tests';

type GuideType = 'NOM35_G1' | 'NOM35_G2' | 'NOM35_G3';

export function Nom35AssessmentPage() {
  const { id } = useParams<{ id: string }>();
  const [status, setStatus] = useState<'loading' | 'invalid' | 'completed' | 'ready'>('loading');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [assessment, setAssessment] = useState<{
    id: string;
    employeeName: string;
    guideType: GuideType;
    status: string;
    nextAssessmentId?: string;
  } | null>(null);
  const { locked: examLocked } = useExamLock(id ?? null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) {
      setStatus('invalid');
      return;
    }
    getDoc(doc(db, 'nom35_assessments', id)).then((snap) => {
      if (!snap.exists()) {
        setStatus('invalid');
        return;
      }
      const data = snap.data();
      const docStatus = data?.status as string;
      const guideType = (data?.guideType as GuideType) ?? 'NOM35_G1';

      setAssessment({
        id: snap.id,
        employeeName: (data?.employeeName as string) ?? 'Trabajador',
        guideType,
        status: docStatus,
        nextAssessmentId: data?.nextAssessmentId as string | undefined,
      });

      if (docStatus === 'completed') {
        setStatus('completed');
      } else {
        setStatus('ready');
      }
    }).catch(() => setStatus('invalid'));
  }, [id]);

  const saveAnswersToFirestore = useCallback(
    async (answers: Record<string, number>) => {
      if (!id) return;
      await updateDoc(doc(db, 'nom35_assessments', id), {
        answers,
        updatedAt: new Date().toISOString(),
      });
    },
    [id]
  );

  const saveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleAnswerChange = useCallback(
    (_itemId: string, _value: number, allAnswers: Record<string, number>) => {
      if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
      saveDebounceRef.current = setTimeout(() => {
        saveAnswersToFirestore(allAnswers);
        saveDebounceRef.current = null;
      }, 1500);
    },
    [saveAnswersToFirestore]
  );

  const handleComplete = async (answers: Record<string, number>) => {
    if (!assessment || !id) return;
    setSubmitError(null);

    const now = new Date().toISOString();
    let resultScore: number | undefined;
    let resultLevel: string | undefined;
    let requiresClinicalAttention: boolean | undefined;

    try {
      if (assessment.guideType === 'NOM35_G1') {
        const r = scoreNom35G1(answers);
        requiresClinicalAttention = r.requiresClinicalAttention;
      } else {
        const items = assessment.guideType === 'NOM35_G2' ? NOM35_G2_ITEMS_FOR_ENGINE : NOM35_G3_ITEMS_FOR_ENGINE;
        const answersArray = items.map((item) => answers[item.id] ?? 0);
        const r = assessment.guideType === 'NOM35_G2'
          ? scoreNom35G2(answersArray, items)
          : scoreNom35G3(answersArray, items);
        resultScore = r.total;
        resultLevel = r.riskLevel;
      }

      const updateData: Record<string, unknown> = {
        status: 'completed',
        completedAt: now,
        answers,
      };
      if (resultScore != null) updateData.resultScore = resultScore;
      if (resultLevel != null) updateData.resultLevel = resultLevel;
      if (requiresClinicalAttention != null) updateData.requiresClinicalAttention = requiresClinicalAttention;

      await updateDoc(doc(db, 'nom35_assessments', id), updateData);

      if (assessment.nextAssessmentId) {
        navigate(`/assessment/${assessment.nextAssessmentId}`, { replace: true });
      } else {
        setStatus('completed');
      }
    } catch (err) {
      console.error('[NOM-035] Error al guardar evaluación:', err);
      setSubmitError(err instanceof Error ? err.message : 'Error al guardar. Revisa la consola.');
    }
  };

  if (status === 'loading') {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <p className="text-muted-foreground">Cargando evaluación...</p>
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="text-center">
          <p className="text-destructive font-medium">Enlace inválido o expirado</p>
          <p className="mt-2 text-sm text-muted-foreground">
            El enlace de evaluación no es válido. Contacta a tu área de Recursos Humanos.
          </p>
        </div>
      </div>
    );
  }

  if (status === 'completed') {
    return (
      <div className="mx-auto max-w-2xl space-y-6 px-4 py-12">
        <div className="rounded-lg border bg-green-50 p-6 text-center dark:bg-green-950/30">
          <h2 className="text-xl font-semibold text-green-800 dark:text-green-200">
            Evaluación completada
          </h2>
          <p className="mt-2 text-green-700 dark:text-green-300">
            Gracias por completar tu evaluación NOM-035. Tus respuestas han sido registradas de forma anónima.
          </p>
        </div>
      </div>
    );
  }

  if (!assessment || status !== 'ready') return null;

  if (examLocked) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="mx-auto max-w-md rounded-lg border border-amber-200 bg-amber-50 p-6 text-center dark:border-amber-800 dark:bg-amber-950/30">
          <h2 className="text-lg font-semibold text-amber-800 dark:text-amber-200">
            Evaluación abierta en otra pestaña
          </h2>
          <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">
            Esta evaluación ya está en curso en otra ventana o pestaña. Cierra la otra pestaña para continuar aquí.
          </p>
        </div>
      </div>
    );
  }

  const items =
    assessment.guideType === 'NOM35_G1'
      ? NOM35_G1_ITEMS_FOR_ENGINE
      : assessment.guideType === 'NOM35_G2'
        ? NOM35_G2_ITEMS_FOR_ENGINE
        : NOM35_G3_ITEMS_FOR_ENGINE;

  const commercialName = getTestDisplayName(assessment.guideType);

  return (
    <div className="mx-auto max-w-3xl space-y-6 px-4 py-8">
      <div className="text-center">
        <h1 className="text-xl font-semibold">Evaluación NOM-035-STPS-2018</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Hola, {assessment.employeeName}. Esta evaluación es confidencial y anónima.
        </p>
        {assessment.nextAssessmentId && (
          <p className="mt-2 text-xs text-muted-foreground">
            Al finalizar esta parte continuarás automáticamente con la siguiente.
          </p>
        )}
      </div>
      {submitError && (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          {submitError}
        </div>
      )}
      <TestEngine
        testId={assessment.guideType}
        commercialName={commercialName}
        items={items}
        onComplete={handleComplete}
        checkFinishCondition={
          assessment.guideType === 'NOM35_G1'
            ? (ca, aa, ci) => nom35EarlyExitCheck(ca, aa, ci, assessment.guideType)
            : undefined
        }
        onAnswerChange={handleAnswerChange}
        persistKey={id ? `nom35_${id}` : undefined}
      />
    </div>
  );
}
